/**
 * ============================================================================
 * usePolicies - PRODUCTION-READY POLICY HOOKS
 * ============================================================================
 *
 * TanStack Query hooks for Cancellation & Refund Policies with:
 * - Smart caching (15min for templates, 10min for venue policies)
 * - Separate queries per booking type (one_time, recurring, membership)
 * - NO unnecessary refetching on tab switches
 * - Proper mutation handling with auto-invalidation
 *
 * CACHING STRATEGY:
 * - Templates: 15min cache (rarely change)
 * - Venue policies: 10min cache (change occasionally)
 * - Separate cache keys per booking type to prevent refetching
 * - Mutations invalidate only relevant queries
 *
 * FIX: Eliminates jitter/refetching when switching between policy tabs
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export type BookingType = 'one_time' | 'recurring' | 'membership';

interface PolicyTemplate {
  id: number;
  name: string;
  description: string;
  booking_type: BookingType;
  config: any;
}

export interface VenuePolicy {
  id: number;
  venue_id: number;
  booking_type: BookingType;
  is_active: boolean;
  name: string;

  // No-refund flag
  accept_cancellations?: boolean; // When false, no refund given regardless of timing

  // One-time booking fields - tier hours and percentages
  tier_1_hours?: number | null;
  tier_1_percentage?: number | null; // Default 100%
  tier_2_hours?: number | null;
  tier_2_percentage?: number | null; // Default 75%
  tier_3_hours?: number | null;
  tier_3_percentage?: number | null; // Default 50%

  // Legacy fields (for backward compatibility)
  full_refund_hours: number;
  partial_refund_hours: number;
  no_refund_hours: number;
  partial_refund_percentage: number;

  // Reschedule fields
  allow_reschedule: boolean;
  max_reschedule_count: number;
  reschedule_threshold: number;
  reschedule_fee: number;

  // Recurring booking - Series cancellation
  allow_series_cancellation: boolean;
  series_cancel_threshold: number;
  series_tier_1_hours?: number | null;
  series_tier_2_hours?: number | null;
  series_tier_3_hours?: number | null;

  // Recurring booking - Individual session cancellation
  allow_session_cancellation: boolean;
  max_sessions_cancellable: number;
  session_tier_1_hours?: number | null;
  session_tier_2_hours?: number | null;
  session_tier_3_hours?: number | null;

  // Recurring booking - Remaining sessions cancellation
  allow_remaining_cancellation: boolean;
  remaining_cancel_threshold: number;
  remaining_tier_1_hours?: number | null;
  remaining_tier_2_hours?: number | null;
  remaining_tier_3_hours?: number | null;

  // Recurring booking - Session reschedule
  allow_session_reschedule: boolean;
  session_reschedule_threshold?: number | null;
  max_reschedules_per_session: number;
  max_total_reschedules: number;

  // Metadata
  refund_processing_days: string;
  advanced_rules?: any;
  created_at?: string;
  updated_at?: string;
}

interface PolicyData {
  templates: PolicyTemplate[];
  venuePolicies: VenuePolicy[];
  activePolicy: VenuePolicy | null;
}

/**
 * Fetch policy templates for a specific booking type
 * Cached per booking type to prevent refetching on tab switches
 */
async function fetchPolicyTemplates(bookingType: BookingType): Promise<PolicyTemplate[]> {
  const response = await fetch(
    `${API_BASE_URL}/v1/policy-templates?booking_type=${bookingType}`
  );

  if (!response.ok) {
    throw new Error('Failed to fetch policy templates');
  }

  const data = await response.json();
  return data.data || [];
}

/**
 * Fetch venue policies for a specific booking type
 * Cached per booking type to prevent refetching on tab switches
 */
async function fetchVenuePolicies(
  venueId: number,
  bookingType: BookingType,
  token: string
): Promise<{ policies: VenuePolicy[]; activePolicy: VenuePolicy | null }> {
  const response = await fetch(
    `${API_BASE_URL}/vendor/venues/${venueId}/policies`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch venue policies');
  }

  const data = await response.json();
  const allPolicies = data.data?.policies || [];

  // Filter by booking type and find active
  const policies = allPolicies.filter((p: VenuePolicy) => p.booking_type === bookingType);
  const activePolicy = policies.find((p: VenuePolicy) => p.is_active) || null;

  return { policies, activePolicy };
}

/**
 * Main hook for fetching all policy data for a specific booking type
 * Uses separate cache keys per booking type to prevent unnecessary refetching
 */
export function usePolicyData(venueId: number | undefined, bookingType: BookingType) {
  const token = sessionStorage.getItem('vendor_token') || '';

  return useQuery<PolicyData, Error>({
    queryKey: ['policies', venueId, bookingType],

    queryFn: async () => {
      if (!venueId) throw new Error('Venue ID is required');

      // Fetch templates and venue policies in parallel
      const [templates, { policies: venuePolicies, activePolicy }] = await Promise.all([
        fetchPolicyTemplates(bookingType),
        fetchVenuePolicies(venueId, bookingType, token),
      ]);

      return {
        templates,
        venuePolicies,
        activePolicy,
      };
    },

    enabled: !!venueId,
    staleTime: 10 * 60 * 1000, // 10 minutes - policies don't change frequently
    gcTime: 20 * 60 * 1000, // 20 minutes in cache
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch if data is fresh (prevents jitter on tab switch)
  });
}

/**
 * Mutation for creating a policy from template
 */
export function useCreatePolicyFromTemplate() {
  const queryClient = useQueryClient();
  const token = sessionStorage.getItem('vendor_token') || '';

  return useMutation({
    mutationFn: async ({
      venueId,
      templateId,
      bookingType,
    }: {
      venueId: number;
      templateId: number;
      bookingType: BookingType;
    }) => {
      const response = await fetch(`${API_BASE_URL}/vendor/venues/${venueId}/policies/from-template`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          template_id: templateId,
          activate: true,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create policy');
      }

      return response.json();
    },

    onSuccess: (data, variables) => {
      // Only invalidate the specific booking type's cache
      queryClient.invalidateQueries({
        queryKey: ['policies', variables.venueId, variables.bookingType],
      });
    },
  });
}

/**
 * Mutation for creating a custom policy
 */
export function useCreateCustomPolicy() {
  const queryClient = useQueryClient();
  const token = sessionStorage.getItem('vendor_token') || '';

  return useMutation({
    mutationFn: async ({
      venueId,
      bookingType,
      config,
    }: {
      venueId: number;
      bookingType: BookingType;
      config: any;
    }) => {
      const url = `${API_BASE_URL}/vendor/venues/${venueId}/policies/custom`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(config),
      });

      // Get the raw response text first
      const responseText = await response.text();

      // Try to parse as JSON
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.error('🔴 [Policy Save] Failed to parse response as JSON. Response is HTML or invalid.');
        console.error('🔴 [Policy Save] Full response:', responseText);
        throw new Error(`Server returned invalid response (status ${response.status}). Check if the API server is running correctly.`);
      }

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to save policy');
      }

      return responseData;
    },

    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['policies', variables.venueId, variables.bookingType],
      });
    },
  });
}

/**
 * Mutation for activating a policy
 */
export function useActivatePolicy() {
  const queryClient = useQueryClient();
  const token = sessionStorage.getItem('vendor_token') || '';

  return useMutation({
    mutationFn: async ({
      venueId,
      policyId,
      bookingType,
    }: {
      venueId: number;
      policyId: number;
      bookingType: BookingType;
    }) => {
      const response = await fetch(
        `${API_BASE_URL}/vendor/venues/${venueId}/policies/${policyId}/activate`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to activate policy');
      }

      return response.json();
    },

    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['policies', variables.venueId, variables.bookingType],
      });
    },
  });
}

/**
 * Mutation for deleting a policy
 */
export function useDeletePolicy() {
  const queryClient = useQueryClient();
  const token = sessionStorage.getItem('vendor_token') || '';

  return useMutation({
    mutationFn: async ({
      venueId,
      policyId,
      bookingType,
    }: {
      venueId: number;
      policyId: number;
      bookingType: BookingType;
    }) => {
      const response = await fetch(`${API_BASE_URL}/vendor/venues/${venueId}/policies/${policyId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete policy');
      }

      return response.json();
    },

    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['policies', variables.venueId, variables.bookingType],
      });
    },
  });
}
